import { useSyncExternalStore } from 'react';

export type ThemeChoice = 'system' | 'dark' | 'light' | 'warm' | 'contrast' | 'glass';

const STORAGE_KEY = 'hub.theme';
const choices: ThemeChoice[] = ['system', 'dark', 'light', 'warm', 'contrast', 'glass'];
const listeners = new Set<() => void>();

function readChoice(): ThemeChoice {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (choices.includes(saved as ThemeChoice)) return saved as ThemeChoice;
  } catch {
    // The selected theme still works for this session when storage is unavailable.
  }
  return 'system';
}

let choice = readChoice();

function systemIsDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function isDarkTheme(): boolean {
  return choice === 'dark' || choice === 'contrast' || (choice === 'system' && systemIsDark());
}

function applyTheme(): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.dataset.theme = choice;
  root.classList.toggle('dark', isDarkTheme());
  root.style.colorScheme = isDarkTheme() ? 'dark' : 'light';
}

function notify(): void {
  applyTheme();
  listeners.forEach((listener) => listener());
}

export function setThemeChoice(next: ThemeChoice): void {
  if (choice === next) return;
  choice = next;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Applying the choice must not depend on persistent storage.
  }
  notify();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  const media = window.matchMedia('(prefers-color-scheme: dark)');
  const onSystemChange = () => {
    if (choice === 'system') notify();
  };
  const onStorage = (event: StorageEvent) => {
    if (event.key !== null && event.key !== STORAGE_KEY) return;
    choice = readChoice();
    notify();
  };
  media.addEventListener('change', onSystemChange);
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    media.removeEventListener('change', onSystemChange);
    window.removeEventListener('storage', onStorage);
  };
}

export function useThemeChoice(): ThemeChoice {
  const snapshot = useSyncExternalStore(
    subscribe,
    () => `${choice}:${isDarkTheme()}`,
    () => 'system:false',
  );
  return snapshot.split(':')[0] as ThemeChoice;
}

export function initializeTheme(): void {
  applyTheme();
}
